import { IconChevronLeft16 } from '@warp-ds/icons/react';
import { Alert, Button, TextArea } from '@warp-ds/react';
import cn from 'classnames';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { Timeline } from 'shared/components/Timeline/Timeline';
import { useConfig } from 'shared/config/ConfigContext';
import { TJVR_RULES_ON_HELPCENTER } from '@/constants';
import type { PriceCleaningChoice } from '@/domain';
import type { User } from '@/domain/userLookup';
import { useObject } from '@/hooks/useObject';
import { usePrivateItemPrice } from '@/hooks/usePrivateItemPrice';
import { isValidDate, splitPhoneNumber } from '@/lib/order/validation';
import { useCleaningChoice } from '@/scenes/createRentalRequestPage/hooks/useCleaningChoice';
import { useRentalRequestSubmit } from '@/scenes/createRentalRequestPage/hooks/useRentalRequestSubmit';
import { useRentalRequestTracking } from '@/scenes/createRentalRequestPage/hooks/useRentalRequestTracking';
import { useRentalRequestValidation } from '@/scenes/createRentalRequestPage/hooks/useRentalRequestValidation';
import styles from './CreateRentalRequestPage.module.css';
import { AdditionalServicesSection } from './components/AdditionalServicesSection';
import { PersonalInfoFields } from './components/PersonalInfoFields';
import { RequestSummaryPanel } from './components/RequestSummaryPanel';
import { useRestoreLastRequestPersonalInfo } from './hooks/useLastRequestPersonalInfo';

export const CreateRentalRequestPage = ({ user }: { user: User | null }) => {
    const objectData = useObject();
    const { baseUrl, contextPath, transactionExternalUrl } = useConfig();
    const { adId, images } = objectData;
    const router = useRouter();
    const { startDate, endDate, guests } = router?.query || {};
    const [firstname, setFirstname] = useState(user?.firstName ?? '');
    const [surname, setSurname] = useState(user?.surname ?? '');
    const [dateOfBirth, setDateOfBirth] = useState<string>('');
    const [landcode, setLandCode] = useState(splitPhoneNumber(user?.phone)?.landCode ?? '+47');
    const [phone, setPhone] = useState(splitPhoneNumber(user?.phone)?.basePhoneNumber ?? '');
    const [message, setMessage] = useState('');
    const [cleaningChoice, setCleaningChoice] = useState<PriceCleaningChoice | null>(null);
    const [priceDetailsExpanded, setPriceDetailsExpanded] = useState(false);

    const {
        shouldValidate,
        enableValidation,
        isFormValid,
        getFieldError,
        isFieldInvalid,
        hasUrlParamsError,
        startDateFormatted,
        endDateFormatted,
    } = useRentalRequestValidation({
        firstname,
        surname,
        dateOfBirth,
        landcode,
        phone,
        message,
        startDate,
        endDate,
        guests,
    });

    const { privateItemPrice } = usePrivateItemPrice(
        adId.toString(),
        isValidDate(startDateFormatted) ? startDateFormatted : new Date(),
        isValidDate(endDateFormatted) ? endDateFormatted : new Date(),
        Number.isNaN(Number(guests)) ? 1 : Number(guests),
        cleaningChoice,
    );

    const { wantsCleaningService, setWantsCleaningService } = useCleaningChoice({ privateItemPrice, setCleaningChoice });

    const { handleSubmit, requestFailed, isSubmitDisabled } = useRentalRequestSubmit({
        user,
        adId,
        startDate,
        endDate,
        guests,
        firstname,
        surname,
        dateOfBirth,
        landcode,
        phone,
        message,
        cleaningChoice,
        privateItemPrice,
        objectData,
        enableValidation,
        isFormValid,
        baseUrl,
        contextPath,
        transactionExternalUrl,
        router,
    });

    useRentalRequestTracking({ adId, isOwner: objectData.user?.isOwner || false });

    useRestoreLastRequestPersonalInfo({
        hasUrlParamsError,
        setFirstname,
        setSurname,
        setDateOfBirth,
        setLandCode,
        setPhone,
        setMessage,
    });

    return (
        <div className={cn('flex flex-col gap-24 pb-144 pt-16 h-full', styles.page)}>
            <Button link className="flex gap-6 items-center mt-8" href={`${contextPath}/ad.html?finnkode=${adId}`}>
                <IconChevronLeft16 aria-hidden /> Tilbake til annonsen
            </Button>
            {hasUrlParamsError ? (
                <Alert type="negative" show={hasUrlParamsError} className="my-32">
                    <strong>Vi mangler gyldig data for ønsket leieperiode og/eller antall gjester</strong>
                    <p className="mt-8">Gå tilbake til annonsen og prøv igjen.</p>
                </Alert>
            ) : (
                <div>
                    <header className={cn(styles.header, 'mb-32 md:mb-0')}>
                        <h1 className="h3 md:h2">Send en uforpliktende forespørsel</h1>
                        <p className="md:max-w-[450px]">Vi trenger bare litt mer informasjon før du sender forespørselen.</p>
                    </header>
                    <div className={cn('flex flex-col md:flex-row-reverse gap-24', styles.content)}>
                        <div className={cn('flex flex-col w-full ', styles.aside)}>
                            <aside className="flex flex-col gap-32 w-full">
                                <RequestSummaryPanel
                                    images={images}
                                    startDate={startDateFormatted}
                                    endDate={endDateFormatted}
                                    guests={guests}
                                    privateItemPrice={privateItemPrice}
                                    priceDetailsExpanded={priceDetailsExpanded}
                                    setPriceDetailsExpanded={setPriceDetailsExpanded}
                                />
                                <Timeline
                                    title="Slik fungerer en forespørsel på FINN"
                                    steps={[
                                        'Du sender en forespørsel til utleier',
                                        'Utleier svarer deg senest innen 48 timer',
                                        'Du velger selv om du vil bekrefte bestillingen',
                                    ]}
                                />
                            </aside>
                        </div>
                        <div className={cn('flex flex-col w-full', styles.formContainer)}>
                            <form onSubmit={handleSubmit} data-testid="rentalRequestForm" className="flex flex-col gap-14">
                                <PersonalInfoFields
                                    firstname={firstname}
                                    setFirstname={setFirstname}
                                    surname={surname}
                                    setSurname={setSurname}
                                    dateOfBirth={dateOfBirth}
                                    setDateOfBirth={setDateOfBirth}
                                    landcode={landcode}
                                    setLandCode={setLandCode}
                                    phone={phone}
                                    setPhone={setPhone}
                                    getFieldError={getFieldError}
                                    isFieldInvalid={isFieldInvalid}
                                />
                                <TextArea
                                    id="message"
                                    name="message"
                                    label="Melding til utleier"
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="Hvem dere er, når dere planlegger å reise og eventuelle spørsmål til utleier"
                                    rows={5}
                                    helpText={
                                        getFieldError('message') ||
                                        'Du vil kunne sende flere meldinger til utleier etter at forespørselen er sendt.'
                                    }
                                    invalid={isFieldInvalid('message')}
                                />
                                {privateItemPrice?.cleaning?.cleaningOption === 'OPTIONAL_GUEST_CLEANS_OR_FEE' && (
                                    <AdditionalServicesSection
                                        privateItemPrice={privateItemPrice}
                                        wantsCleaningService={wantsCleaningService}
                                        setWantsCleaningService={setWantsCleaningService}
                                    />
                                )}
                                <Alert type="negative" show={shouldValidate && !isFormValid}>
                                    Sjekk at alle feltene er fylt ut riktig
                                </Alert>
                                <Alert type="negative" show={requestFailed}>
                                    Noe gikk galt ved sending av forespørsel
                                </Alert>
                                <div className={user ? 'sm:max-w-[200px]' : 'sm:max-w-[320px]'}>
                                    <Button
                                        fullWidth
                                        type="submit"
                                        primary
                                        className={isSubmitDisabled ? 'in-progress' : ''}
                                        disabled={isSubmitDisabled}>
                                        {user ? 'Send forespørsel' : 'Logg inn for å sende forespørsel'}
                                    </Button>
                                </div>
                            </form>
                            <p className="text-12 s-text-subtle my-8">
                                Ved å sende forespørselen godtar du
                                <Button
                                    link
                                    className="text-12 no-linebreak mx-4"
                                    href={TJVR_RULES_ON_HELPCENTER}
                                    target="_blank"
                                    rel="noreferrer">
                                    vilkår for tjenesten
                                </Button>
                                og at du er over 18 år.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
